import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { AlertTriangle, CheckCircle, Search, RefreshCw, Wrench, Database } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DataIntegrity = () => {
  const [loading, setLoading] = useState(false);
  const [missingPlu, setMissingPlu] = useState(null);
  const [duplicatePlu, setDuplicatePlu] = useState(null);
  const [duplicateVat, setDuplicateVat] = useState(null);
  const [fixing, setFixing] = useState(false);
  const [archiving, setArchiving] = useState(null); // product id currently being archived
  const [clearingVat, setClearingVat] = useState(null); // customer id currently being cleared

  const archiveDuplicate = async (productId, productName) => {
    if (!window.confirm(
      `Archive "${productName}"?\n\nThis product will be hidden from Stock Master, search results and promotions. ` +
      `Its sales history is preserved. You can restore it later from the Archived Products list.`
    )) return;
    setArchiving(productId);
    try {
      await axios.post(`${API}/products/admin/bulk-archive`, { product_ids: [productId] });
      toast.success("Product archived. Refreshing…");
      const res = await axios.get(`${API}/products/duplicate-plu`);
      setDuplicatePlu(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to archive product");
    } finally {
      setArchiving(null);
    }
  };

  const checkMissingPlu = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/missing-plu`);
      setMissingPlu(response.data);
    } catch (error) {
      toast.error("Failed to check for missing PLU codes");
    } finally {
      setLoading(false);
    }
  };

  const checkDuplicatePlu = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/duplicate-plu`);
      setDuplicatePlu(response.data);
    } catch (error) {
      toast.error("Failed to check for duplicate PLU codes");
    } finally {
      setLoading(false);
    }
  };

  const fixMissingPlu = async () => {
    if (!window.confirm("This will automatically assign PLU codes to all products without one. Continue?")) {
      return;
    }
    
    setFixing(true);
    try {
      const response = await axios.post(`${API}/products/fix-missing-plu?auto_assign=true`);
      toast.success(`Fixed ${response.data.count} products`);
      setMissingPlu(response.data);
      // Refresh the list
      checkMissingPlu();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to fix missing PLU codes");
    } finally {
      setFixing(false);
    }
  };

  const runAllChecks = async () => {
    setLoading(true);
    try {
      const [missing, duplicates, vatDupes] = await Promise.all([
        axios.get(`${API}/products/missing-plu`),
        axios.get(`${API}/products/duplicate-plu`),
        axios.get(`${API}/customers/duplicate-vat`),
      ]);
      setMissingPlu(missing.data);
      setDuplicatePlu(duplicates.data);
      setDuplicateVat(vatDupes.data);
    } catch (error) {
      toast.error("Failed to run integrity checks");
    } finally {
      setLoading(false);
    }
  };

  const clearCustomerVat = async (customerId, customerName) => {
    if (!window.confirm(
      `Clear the VAT number on "${customerName}"?\n\nThis will remove it so the number can be assigned to only ONE customer. The rest of the profile is untouched.`
    )) return;
    setClearingVat(customerId);
    try {
      // Fetch the existing customer, wipe the vat_number, PUT it back.
      const cur = await axios.get(`${API}/customers/${customerId}`);
      const payload = { ...cur.data, vat_number: "" };
      // The customer PUT model rejects unknown fields — strip anything
      // that isn't on CustomerCreate.
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.account_number;
      delete payload.calculated_balance;
      await axios.put(`${API}/customers/${customerId}`, payload);
      toast.success(`VAT number cleared on ${customerName}. Refreshing…`);
      const res = await axios.get(`${API}/customers/duplicate-vat`);
      setDuplicateVat(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to clear VAT number");
    } finally {
      setClearingVat(null);
    }
  };

  useEffect(() => {
    runAllChecks();
  }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Database className="h-6 w-6" />
            Data Integrity Check
          </h1>
          <p className="text-gray-500">Find and fix data issues that can cause incorrect reports</p>
        </div>
        <Button onClick={runAllChecks} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Run All Checks
        </Button>
      </div>

      {/* Missing PLU Codes */}
      <Card className={missingPlu?.count > 0 ? "border-red-300 bg-red-50" : "border-green-300 bg-green-50"}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {missingPlu?.count > 0 ? (
              <AlertTriangle className="h-5 w-5 text-red-500" />
            ) : (
              <CheckCircle className="h-5 w-5 text-green-500" />
            )}
            Products Without PLU Codes
          </CardTitle>
          <CardDescription>
            Products without PLU codes can cause data to be incorrectly linked to other products.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Checking...
            </div>
          ) : missingPlu ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-lg font-semibold">
                    {missingPlu.count === 0 ? (
                      <span className="text-green-700">All products have PLU codes ✓</span>
                    ) : (
                      <span className="text-red-700">{missingPlu.count} products need PLU codes</span>
                    )}
                  </p>
                </div>
                {missingPlu.count > 0 && (
                  <Button 
                    onClick={fixMissingPlu} 
                    disabled={fixing}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    <Wrench className={`h-4 w-4 mr-2 ${fixing ? 'animate-spin' : ''}`} />
                    {fixing ? "Fixing..." : "Auto-Fix All"}
                  </Button>
                )}
              </div>
              
              {missingPlu.products?.length > 0 && (
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="text-left p-2">Product Name</th>
                        <th className="text-left p-2">Barcode</th>
                        <th className="text-left p-2">Supplier</th>
                        <th className="text-left p-2">ID</th>
                      </tr>
                    </thead>
                    <tbody>
                      {missingPlu.products.map((product, idx) => (
                        <tr key={idx} className="border-b hover:bg-red-100">
                          <td className="p-2 font-medium">{product.name}</td>
                          <td className="p-2">{product.barcode || "-"}</td>
                          <td className="p-2">{product.supplier || "-"}</td>
                          <td className="p-2 text-xs text-gray-500 font-mono">{product.id?.slice(0,8)}...</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Duplicate PLU Codes */}
      <Card className={duplicatePlu?.count > 0 ? "border-orange-300 bg-orange-50" : "border-green-300 bg-green-50"}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {duplicatePlu?.count > 0 ? (
              <AlertTriangle className="h-5 w-5 text-orange-500" />
            ) : (
              <CheckCircle className="h-5 w-5 text-green-500" />
            )}
            Duplicate PLU Codes
          </CardTitle>
          <CardDescription>
            Multiple products with the same PLU code will have their sales and GRV data mixed together.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Checking...
            </div>
          ) : duplicatePlu ? (
            <div className="space-y-4">
              <p className="text-lg font-semibold">
                {duplicatePlu.count === 0 ? (
                  <span className="text-green-700">No duplicate PLU codes found ✓</span>
                ) : (
                  <span className="text-orange-700">{duplicatePlu.count} PLU codes are duplicated</span>
                )}
              </p>
              
              {duplicatePlu.duplicates?.length > 0 && (
                <div className="space-y-4">
                  {duplicatePlu.duplicates.map((dup, idx) => (
                    <div key={idx} className="border rounded-lg p-3 bg-white" data-testid={`dup-group-${dup._id}`}>
                      <p className="font-semibold text-orange-700 mb-2">
                        {dup.kind === "barcode_vs_plu" ? (
                          <>PLU vs Barcode collision on <b>{dup._id}</b></>
                        ) : (
                          <>PLU: <b>{dup._id}</b> (used by {dup.count} products)</>
                        )}
                      </p>
                      <div className="space-y-2">
                        {dup.products.map((p, pIdx) => {
                          const stock = Number(p.current_stock ?? 0);
                          const sell = p.selling_price ? Number(p.selling_price) : null;
                          const cost = p.last_cost_price_excl_vat != null
                            ? Number(p.last_cost_price_excl_vat)
                            : (p.average_cost_price_excl_vat != null ? Number(p.average_cost_price_excl_vat) : null);
                          const updated = p.last_updated
                            ? new Date(p.last_updated).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                            : "—";
                          return (
                            <div key={pIdx} className="text-sm flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-2 first:border-t-0 first:pt-0">
                              <div className="flex-1 min-w-[240px]">
                                <div className="font-medium">{p.name}</div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  ID: <span className="font-mono">{p.id?.slice(0, 8)}…</span>
                                  {p.plu_code != null && <> · PLU: <span className="font-mono">{String(p.plu_code)}</span></>}
                                  {p.barcode ? <> · Barcode: {p.barcode}</> : null}
                                  {p.supplier ? <> · Supplier: {p.supplier}</> : null}
                                </div>
                                {p.note && (
                                  <div className="text-xs text-blue-700 mt-0.5 italic">{p.note}</div>
                                )}
                              </div>
                              <div className="text-xs text-slate-700 whitespace-nowrap">
                                Stock: <b>{stock}</b>
                                {cost != null && <> · Cost: <b>R{cost.toFixed(2)}</b></>}
                                {sell != null && <> · Sell: <b>R{sell.toFixed(2)}</b></>}
                                <> · Updated: {updated}</>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-700 hover:bg-red-50"
                                disabled={archiving === p.id}
                                onClick={() => archiveDuplicate(p.id, p.name)}
                                data-testid={`archive-dup-${p.id}`}
                              >
                                {archiving === p.id ? "Archiving…" : "Archive this one"}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-orange-600 mt-3">
                        {dup.kind === "barcode_vs_plu"
                          ? "One product uses this value as its PLU while another product uses it as its barcode. The cashier / promotions search will occasionally land on the wrong one. Archive whichever entry shouldn't own this value."
                          : "Keep the entry you want (usually the one with recent stock, cost and last-updated), then archive the rest. Archiving is reversible from the Archived Products page."}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Duplicate Customer VAT Numbers */}
      <Card
        className={duplicateVat?.count > 0 ? "border-orange-300 bg-orange-50" : "border-green-300 bg-green-50"}
        data-testid="dup-vat-card"
      >
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {duplicateVat?.count > 0 ? (
              <AlertTriangle className="h-5 w-5 text-orange-500" />
            ) : (
              <CheckCircle className="h-5 w-5 text-green-500" />
            )}
            Duplicate Customer VAT Numbers
          </CardTitle>
          <CardDescription>
            Two customers should never share a VAT number — SARS reconciliation and Farmer VAT
            certificate audits both rely on it being unique. New customers with a duplicate VAT
            number are now rejected at save time. Existing collisions show below so you can pick
            which one to keep.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Checking...
            </div>
          ) : duplicateVat ? (
            <div className="space-y-4">
              <p className="text-lg font-semibold">
                {duplicateVat.count === 0 ? (
                  <span className="text-green-700">All customer VAT numbers are unique ✓</span>
                ) : (
                  <span className="text-orange-700">
                    {duplicateVat.count} VAT number(s) are shared by more than one customer
                  </span>
                )}
              </p>

              {duplicateVat.duplicates?.length > 0 && (
                <div className="space-y-4">
                  {duplicateVat.duplicates.map((dup, idx) => (
                    <div
                      key={idx}
                      className="border rounded-lg p-3 bg-white"
                      data-testid={`dup-vat-group-${dup._id}`}
                    >
                      <p className="font-semibold text-orange-700 mb-2">
                        VAT: <b>{dup._id}</b> (used by {dup.count} customers)
                      </p>
                      <div className="space-y-2">
                        {dup.customers.map((c, cIdx) => {
                          const bal = Number(c.account_balance ?? 0);
                          return (
                            <div
                              key={cIdx}
                              className="text-sm flex flex-wrap items-center gap-x-4 gap-y-1 border-t pt-2 first:border-t-0 first:pt-0"
                            >
                              <div className="flex-1 min-w-[240px]">
                                <div className="font-medium">
                                  {c.name}
                                  {c.has_vat_exemption ? (
                                    <span className="ml-2 inline-flex items-center rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5 text-xs font-normal">
                                      VAT-exempt cert
                                    </span>
                                  ) : null}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  Account:{" "}
                                  <span className="font-mono">{c.account_number || "—"}</span>
                                  {c.contact_number ? <> · Tel: {c.contact_number}</> : null}
                                  {c.email ? <> · {c.email}</> : null}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  Stored VAT:{" "}
                                  <span className="font-mono">{c.vat_number || "—"}</span>
                                </div>
                              </div>
                              <div className="text-xs text-slate-700 whitespace-nowrap">
                                Balance: <b>R{bal.toFixed(2)}</b>
                              </div>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-300 text-red-700 hover:bg-red-50"
                                disabled={clearingVat === c.id}
                                onClick={() => clearCustomerVat(c.id, c.name)}
                                data-testid={`clear-vat-${c.id}`}
                              >
                                {clearingVat === c.id ? "Clearing…" : "Clear VAT on this one"}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-orange-600 mt-3">
                        Keep the customer whose VAT number is genuinely correct, and clear it from
                        the others. The rest of each profile (balance, transactions, contact) stays
                        untouched.
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Info Box */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <h3 className="font-semibold text-blue-800 mb-2">Why PLU Codes Matter</h3>
          <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
            <li>PLU codes uniquely identify each product in the system</li>
            <li>Sales history, GRV records, and stock movements are linked by PLU code</li>
            <li>Products without PLU codes can have their data incorrectly merged with other products</li>
            <li>Duplicate PLU codes cause the same problem - data gets mixed between products</li>
            <li>After fixing, historical data may still be incorrect - but future data will be accurate</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataIntegrity;
